import { jsx as _jsx } from "react/jsx-runtime";
import path from 'path';
import { parse, stringify } from 'qs';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { useLocation as useLocationV6, useNavigate } from 'react-router-dom-v5-compat';
import { useMount } from 'react-use';
import { BowtieWebPath } from './constants';
import { BaseError } from './error';
export const parseQs = (search, options) => {
  const decodedSearch = search.replace(/&amp;/g, '&');
  const merged = {
    ignoreQueryPrefix: true,
    ...options
  };
  return parse(decodedSearch, merged);
};
export const stripQs = (parsed, whitelistedQs) => {
  const whitelistedKeys = ['app_case_id', 'group_policy_id', ...(whitelistedQs || [])];
  return Object.keys(parsed).reduce((a, q) => {
    if (!whitelistedKeys.includes(q)) {
      a[q] = undefined;
    }

    return a;
  }, { ...parsed
  });
};
export const updateQs = (search, update, stringifyOptions, parseOptions) => {
  const parsed = parseQs(search, parseOptions);
  const updated = typeof update === 'function' ? update(parsed) : { ...parsed,
    ...update
  };
  return stringify(updated, stringifyOptions);
};
export function useQs(options) {
  const {
    search
  } = useLocation();
  const parsed = React.useMemo(() => {
    return parseQs(search, options);
  }, [search, options]);
  return parsed;
}
export function useQsV6(options) {
  const {
    search
  } = useLocationV6();
  const parsed = React.useMemo(() => {
    return parseQs(search, options);
  }, [search, options]);
  return parsed;
}
export const useHash = () => {
  const {
    hash
  } = useLocation();
  return hash.startsWith('#') ? hash.substr(1) : hash;
};
export function useHashQs(options) {
  const hash = useHash();
  const parsed = React.useMemo(() => {
    return parseQs(hash, options);
  }, [hash, options]);
  return parsed;
}
export const useHistoryListen = listener => {
  const callbackRef = React.useRef(listener);
  const history = useHistory();
  callbackRef.current = listener;
  React.useEffect(() => {
    const callback = (location, action) => {
      callbackRef.current(location, action);
    };

    callback(history.location, history.action);
    const unlisten = history.listen(callback);
    return unlisten;
  }, [history]);
};
export const ExternalRedirect = ({
  exact,
  sensitive,
  strict,
  from,
  to
}) => {
  const RedirectComp = () => {
    React.useEffect(() => {
      window.location.assign(to);
    }, []);
    return null;
  };

  return _jsx(Route, {
    path: from,
    exact: exact,
    sensitive: sensitive,
    strict: strict,
    component: RedirectComp
  });
};
export const ExternalRedirectV6 = ({
  to
}) => {
  React.useEffect(() => {
    window.location.assign(to);
  }, [to]);
  return null;
};
export const createFullPath = (location, update) => {
  const stringified = location.toString();

  if (update) {
    const url = new URL(stringified);
    Object.entries(update).forEach(([key, value]) => {
      url[key] = value;
    });
    return url.toString();
  }

  return stringified;
};
export const makeAppUrl = (pathname, lng) => {
  const {
    protocol,
    host,
    pathname: locationPathName
  } = window.location;
  const localePath = lng ? `/#/${lng}/${pathname}` : pathname;
  const pathMatches = locationPathName.match(/[^(\/)]\w*/g);
  const base = process.env.REACT_APP_ENVIRONMENT === 'pull-request' && pathMatches ? path.join(host, 'app', pathMatches[pathMatches.length - 1]) : host;
  return `${protocol}//${path.join(base, localePath)}`;
};
export const appLoginLoc = (prevLocation, options) => {
  const location = {
    pathname: BowtieWebPath.App.Login,
    search: updateQs(prevLocation.search || '', {
      next: (options === null || options === void 0 ? void 0 : options.next) || prevLocation.pathname
    })
  };
  if (options === null || options === void 0 ? void 0 : options.loginUrl) location.pathname = options.loginUrl;
  if (options === null || options === void 0 ? void 0 : options.withState) location.state = {
    from: prevLocation.pathname
  };
  if (options === null || options === void 0 ? void 0 : options.state) location.state = options === null || options === void 0 ? void 0 : options.state;
  return location;
};
export const removeLeadingSlash = path => path.startsWith('/') ? path.slice(1) : path;
export const addTrailingSlash = path => path.endsWith('/') ? path : `${path}/`;
export const removeTrailingSlash = path => path.endsWith('/') ? path.slice(0, -1) : path;
export const isPathPrefixed = (path, prefix) => addTrailingSlash(path).startsWith(addTrailingSlash(prefix));
export class PrefixMismatchError extends BaseError {
  constructor(message = 'location.pathname does not start with prefix provided') {
    super(message);
    this.name = 'PrefixMismatchError';
  }

}
export function usePathname({
  initialState,
  prefix: prefixProp = '',
  formatter = suffix => suffix
}) {
  const prefix = removeTrailingSlash(prefixProp);
  const location = useLocation();
  const history = useHistory();
  const pathnameWithoutPrefix = useMemo(() => {
    if (!isPathPrefixed(location.pathname, prefix)) {
      throw new PrefixMismatchError(`location.pathname (${location.pathname}) does not start with prefix (${prefix}) provided.`);
    }

    return removeLeadingSlash(location.pathname.replace(prefix, ''));
  }, [location.pathname, prefix]);
  const state = useMemo(() => formatter(pathnameWithoutPrefix), [formatter, pathnameWithoutPrefix]);
  const setState = useCallback(nextState => {
    const nextSuffix = removeLeadingSlash(pathnameWithoutPrefix.replace(state, nextState));
    const pathname = `${prefix}${nextSuffix ? `/${nextSuffix}` : ''}`;
    history.push({ ...location,
      pathname
    });
  }, [history, location, pathnameWithoutPrefix, prefix, state]);
  useMount(() => {
    if (initialState !== undefined) {
      setState(initialState);
    }
  });
  return useMemo(() => [state, setState], [setState, state]);
}
export const usePathnameV6 = ({
  initialState,
  prefix: prefixProp = '',
  formatter = suffix => suffix
}) => {
  const prefix = removeTrailingSlash(prefixProp);
  const location = useLocationV6();
  const navigate = useNavigate();
  const pathnameWithoutPrefix = useMemo(() => {
    if (!isPathPrefixed(location.pathname, prefix)) {
      throw new PrefixMismatchError(`location.pathname (${location.pathname}) does not start with prefix (${prefix}) provided.`);
    }

    return removeLeadingSlash(location.pathname.replace(prefix, ''));
  }, [location.pathname, prefix]);
  const state = useMemo(() => formatter(pathnameWithoutPrefix), [formatter, pathnameWithoutPrefix]);
  const setState = useCallback(nextState => {
    const nextSuffix = removeLeadingSlash(pathnameWithoutPrefix.replace(state, nextState));
    const pathname = `${prefix}${nextSuffix ? `/${nextSuffix}` : ''}`;
    navigate({ ...location,
      pathname
    }, {
      state: location.state
    });
  }, [pathnameWithoutPrefix, state, prefix, navigate, location]);
  useMount(() => {
    if (initialState !== undefined) {
      setState(initialState);
    }
  });
  return useMemo(() => [state, setState], [setState, state]);
};
export const getSubProjectUrl = (appName, pathname) => {
  var _locationPathName$mat;

  const {
    protocol,
    host,
    pathname: locationPathName
  } = window.location;
  const commitHash = (_locationPathName$mat = locationPathName.match(/[^(\/)]\w*/g)) === null || _locationPathName$mat === void 0 ? void 0 : _locationPathName$mat[1];
  const base = process.env.REACT_APP_ENVIRONMENT === 'pull-request' && commitHash ? path.join(host, appName, commitHash) : path.join(host, appName);
  return `${protocol}//${path.join(base, pathname)}`;
};